import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Card,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { updateUser } from 'services/userService';

const validationSchema = Yup.object({
  userEmail: Yup.string().email('Must be a valid email').required('Email is required'),
});

function UserEdit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const tenantId = searchParams.get('tenantId');
  const currentEmail = searchParams.get('email');

  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formik = useFormik({
    initialValues: {
      userEmail: currentEmail || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        await updateUser(username, {
          userEmail: values.userEmail,
          tenantId: tenantId,
        });
        setSnackbar({ open: true, message: 'User email updated successfully!', severity: 'success' });
        setTimeout(() => navigate('/users'), 1500);
      } catch (err) {
        console.error('Error updating user:', err);
        setSnackbar({ open: true, message: 'Failed to update user', severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (!username || !tenantId) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Alert severity="error">Missing user information. Please go back and try again.</Alert>
        </SoftBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" mb={2}>Edit User</SoftTypography>

              <SoftBox mb={2}>
                <SoftTypography variant="caption" fontWeight="bold">Username</SoftTypography>
                <SoftInput value={username} disabled />
              </SoftBox>

              <form onSubmit={formik.handleSubmit}>
                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Email *</SoftTypography>
                  <SoftInput
                    placeholder="user@example.com"
                    name="userEmail"
                    type="email"
                    value={formik.values.userEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.userEmail && Boolean(formik.errors.userEmail)}
                  />
                  {formik.touched.userEmail && formik.errors.userEmail && (
                    <SoftTypography variant="caption" color="error">{formik.errors.userEmail}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox display="flex" gap={2}>
                  {!submitting ? (
                    <>
                      <SoftButton type="submit" variant="gradient" color="info" disabled={!formik.isValid || !formik.dirty}>
                        Save
                      </SoftButton>
                      <SoftButton variant="gradient" color="secondary" onClick={() => navigate('/users')}>
                        Cancel
                      </SoftButton>
                    </>
                  ) : (
                    <CircularProgress size={24} />
                  )}
                </SoftBox>
              </form>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default UserEdit;
